import {
  Stack,
  Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import { ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AgendaWidgetContainerStyle,
  Appointment,
  AppointmentList,
  TodayLabel,
  TypographyAppointmentHours,
  TypographyAppointmentInfo,
  TypographyAppointmentTitle,
  TypographyTodayAgenda
} from "./AgendaWidget.styles";
import { courseColor, expiringLabel, goToDetail, handleType, isTodayAssertion } from "./utils";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CONTENT_TYPE } from "../../consts";
import { useDateFnsLocale } from "../../hooks";
import { useContentQuery } from "../../queries";
import { useNavigate } from "../../services";
import { useLanguageStore } from "../../stores";
import type {
  CalendarLearningObject,
  DayToHighlight
} from "../../types";
import { CommonWidgetContainer } from "../Commons/CommonWidgetContainer.styles";


export const AgendaWidgetContainer = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const language = useLanguageStore(state => state.language)?.id ?? "it";
  //const language = useLanguageStore(state => state.language)?.id;
  const locale = useDateFnsLocale({ localeCode: language || "it" }); //it come default, ma appena arriva la response dal servizio languages, si aggiorna con il primary_language

  // const currentDay = new Date();

  // const { data: pageStructureWidgetAgenda } = useStructuresQuery({
  //   pageName: PAGE_NAME.WIDGET_AGENDA
  // });
  // const agendaContentsPath = pageStructureWidgetAgenda?.relativePaths?.find(
  //   (relativePath) => (relativePath.serviceType === WIDGET.CALENDAR)
  // );

  //const widgetSelected = format(new Date(), "yyyy/MM/dd");
  const widgetStartDate =
  format(new Date(`${format(new Date(), "yyyy")}/${format(new Date(), "MM")}/${format(new Date(), "dd")}`), "yyyy/MM/dd");

  // gestione data di tre mesi dopo la data odierna
  const threeMonthsFromNow = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d.toISOString().split("T")[0];
  })();

  const {
    data: learnerAgendaDaysWidget
  } = useContentQuery<{ daysToHighlight: DayToHighlight[] }>({
    enabled,
    path: `/learning-catalogue/{corporateId}/{initiativeId}/learnerElementsAgenda?startDate=${widgetStartDate?.replaceAll("/", "-")}&endDate=${threeMonthsFromNow}&pageNumber=${0}&pageSize=${8}`,
    queryKey: [language]
  });

  function arrayDateToTimestamp(year: number, month:number, day:number) {
    return new Date(year, month - 1, day).getTime();
  }

  //@ts-ignore
  function handleApointments(day) {
    let newObj = [];
    let completeDate = null;

    // check se sono presenti smartslots, per unirli ai learningObjects
    if (day.areThereSmartSlots && day?.smartLearningSlots?.length > 0) {
      let dayCheck = day.day;
      const daySmartSlot = arrayDateToTimestamp(dayCheck[0], dayCheck[1], dayCheck[2]);

      newObj = day.learningObjects
        ? [...day.learningObjects, ...day.smartLearningSlots]
        : [...day.smartLearningSlots];

      return {
        ...day,
        appointments: newObj,
        completeDate: daySmartSlot
      };
    }
    if(day.learningObjects?.length > 0) {

      newObj = day.learningObjects && [...day.learningObjects];
      completeDate = day.learningObjects[0].startDateTime;
    }
    return {
      ...day,
      appointments: newObj,
      completeDate: completeDate
    };
  }

  const appointments = useMemo<DayToHighlight[]>(() => {
    if(!learnerAgendaDaysWidget?.daysToHighlight) return [];

    return learnerAgendaDaysWidget?.daysToHighlight
      .map(handleApointments)
      .filter(
        (day): day is DayToHighlight =>
          day.appointments?.length > 0
      );
  }, [learnerAgendaDaysWidget]);

  function getAppointmentTime(lo: CalendarLearningObject) {
    const startDateTime = format(new Date(lo.startDateTime), "HH:mm");
    const endDateTime = format(new Date(lo.endDateTime), "HH:mm");
    return `${startDateTime} - ${endDateTime}`;
  }

  function normalizeTime(time:string) {
    const [hours, minutes] = time.split(":").map(s => s.trim());
    return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  }

  function handleTimeString(lo:CalendarLearningObject) {
    if (lo.objectType === CONTENT_TYPE.SMART_LEARNING_SLOT) {
      return `${normalizeTime(lo.startTime)} - ${normalizeTime(lo.endTime)}`;
    }
    return getAppointmentTime(lo);
  }

  function capitalizeMonth(dateString: string) {
    return dateString.replace(
      /^(\d+\s+)(\p{L}+)/u,
      (_, dayPart, month) =>
        `${dayPart}${month[0].toUpperCase()}${month.slice(1)}`
    );
  }

  const thereAreApointmentToday =()=> appointments.some((day) => isTodayAssertion(day.completeDate ?? 0));

  function formatItalianDate(): JSX.Element {
    const formatted = new Intl.DateTimeFormat(language, {
      day: "numeric",
      month: "long",
      year: "numeric"
    }).format(new Date());

    const capitalized = capitalizeMonth(formatted);
    return (
      <TypographyTodayAgenda>
        <Typography
          component="span"
          fontSize="0.875rem"
          lineHeight="1.25rem"
          fontWeight={ 600 }
          marginRight="0.25rem"
          id={ "today-agenda-date" }>
          { `${ t("today")}` }
        </Typography>
        <Typography
          component="span"
          fontSize="0.875rem"
          lineHeight="1.25rem"
          fontWeight={ 400 }>
          { `| ${capitalized}` }
        </Typography>
      </TypographyTodayAgenda>
    );
  }

  return (
    <CommonWidgetContainer id="agenda-widget" hasSidePadding={ false } isLoading={ false } sx={ { gap: "0rem" } }>
      { appointments.length === 0 ? (
        <Stack
          fontSize="0.875rem"
          lineHeight="1.25rem"
          display="flex"
          flexDirection="column"
          gap="1rem"
          fontWeight={ 400 }
          padding="0rem 1rem"
          role="presentation"
          tabIndex={ 0 }
          aria-labelledby={ "today-agenda-date" }
        >
          <Typography
            fontSize="0.875rem"
            lineHeight="1.25rem"
            fontWeight={ 600 }
            marginRight="0.25rem"
            id={ "today-agenda-date" }>
            { formatItalianDate() }
          </Typography>

          <Typography
            fontSize="0.875rem"
            lineHeight="1.25rem"
          >
            { t("no_appointments_in_agenda") }
          </Typography>
        </Stack>
      ) : (
        <>
          {
            !thereAreApointmentToday() && (
              <TodayLabel
                fontSize="0.875rem"
                lineHeight="1.25rem"
                display="flex"
                flexDirection="column"
                gap="0.75rem"
                fontWeight={ 400 }
                padding="0rem 1rem 0.5rem"
                role="presentation"
                tabIndex={ 0 }
                aria-labelledby={ "today-agenda-date" }
              >
                <Typography
                  fontSize="0.875rem"
                  lineHeight="1.25rem"
                  fontWeight={ 600 }
                  marginRight="0.25rem"
                  id={ "today-agenda-date" }>
                  { formatItalianDate() }
                </Typography>
              </TodayLabel>
            ) }
          {
            appointments?.map((day: DayToHighlight, index: number) => {
              const fixedDate = day.day;

              const [year, month, dayInt] = fixedDate;
              const appointmentDate = format(
                new Date(year, month - 1, dayInt),
                "d MMMM y",
                { locale }
              );

              let isToday: boolean = isTodayAssertion(day.completeDate ?? 0);

              return (
                <AgendaWidgetContainerStyle key={ `day-${day.completeDate}-agenda--${language}` }>
                  <Stack
                    fontSize="0.875rem"
                    lineHeight="1.25rem"
                    display="flex"
                    flexDirection="row"
                    fontWeight={ 400 }
                    padding="0rem 0.5rem"
                    id={ "appointment-date-" + index + "-" + day.completeDate }>
                    { isToday && (
                      <>
                        <Typography
                          fontSize="0.875rem"
                          lineHeight="1.25rem"
                          fontWeight={ 600 }
                          marginRight="0.25rem">
                          { `${ t("today")}` }
                        </Typography>
                        <Typography
                          fontSize="0.875rem"
                          lineHeight="1.25rem"
                          marginRight="0.25rem"
                        >
                          { "|" }
                        </Typography>
                      </>
                    ) }
                    <Typography
                      fontSize="0.875rem"
                      lineHeight="1.25rem"
                      id={ "today-agenda-date" }
                    >
                      { capitalizeMonth(appointmentDate) }
                    </Typography>
                  </Stack>
                  { /* lista appuntamenti */ }
                  <AppointmentList role="list">
                    { day.appointments?.map((lo: CalendarLearningObject, appointmentInd:number) => {
                      return (
                        <Appointment
                          key={ appointmentInd }
                          // isDigital={ lo.typeDigital }
                          color={ courseColor(lo, theme) }
                          tabIndex={ 0 }
                          aria-labelledby={ "agenda-appointment-type-" + lo.id + " " + "agenda-label-" + lo.id }

                          onClick={ () => goToDetail(lo, navigate, lo.dateObjectType ?? undefined) }
                        >
                          <div style={ { width: "100%" } }>
                            <TypographyAppointmentTitle
                            // isDigital={ lo.typeDigital }
                              id={ "agenda-appointment-type-" + lo.id }
                              color={ courseColor(lo, theme) }
                            >
                              { /* @ts-ignore */ }
                              { expiringLabel(lo.learningObjectTypology || lo.objectType) }
                              { /* @ts-ignore */ }
                              { handleType(lo.learningObjectType || lo.objectType, lo.learningObjectTypology).toUpperCase() }
                            </TypographyAppointmentTitle>
                            <div style={ {
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%"
                            } }>
                              <TypographyAppointmentInfo
                                style={ { flexGrow: 1 } }
                                id={ "agenda-label-" + lo.id }
                                aria-label={ lo.title }
                              >
                                { lo.title ?? t("learning_time") }
                              </TypographyAppointmentInfo>
                              {
                                ((lo.startDateTime && lo.endDateTime) || (lo.startTime && lo.endTime)) && (
                                  <TypographyAppointmentHours
                                    style={ { minWidth: "4.8rem", marginLeft: "3rem" } }
                                    aria-label={  handleTimeString(lo) }
                                  >
                                    { handleTimeString(lo) }
                                  </TypographyAppointmentHours>

                                )
                              }
                            </div>
                          </div>
                        </Appointment>
                      );
                    }) }
                  </AppointmentList>

                </AgendaWidgetContainerStyle>
              );
            })
          }
        </>
      ) }

      <ButtonDigited href="/agenda" typology="secondary" buttonSize="sm"  selfPosition="flex-end" sx={ { marginRight:"1rem", marginTop:"0.5rem" } }>
        <>
          { t("go_to_agenda") + " " } <ChevronRight size="0.875rem" />
        </>
      </ButtonDigited>
    </CommonWidgetContainer>
  );
};

